/**
 * ConfigHistoryTimeline.js
 *
 * A reusable, vertically-scrollable timeline that displays audit trail
 * events from the organisations/{orgId}/configHistory subcollection.
 *
 * Used inside ManageOrgConfigDialog, ManageNotificationsDialog, and
 * ManageInstrumentsDialog to show a "View History" panel.
 *
 * When an event contains `previousValues` and `newValues` maps, a compact
 * before → after diff is rendered for each changed field, using
 * human-readable labels resolved from the notification and instrument
 * settings configs.
 *
 * Props:
 *   sections        - array of section strings to filter by (passed to CF)
 *   onBack          - callback to navigate back to the parent menu
 *   title           - heading shown above the timeline (e.g. "Change History")
 *   onContentChange - optional callback fired when content finishes loading,
 *                     allowing the parent dialog to re-measure its animated
 *                     height after the async data arrives.
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase';

// ─── Human-readable labels for config sections ──────────────────────────
const SECTION_LABELS = {
  supportContacts: 'Support Contacts',
  letterhead: 'Letterhead',
  branding: 'Branding',
  notificationConfig: 'Notification Preferences',
  instruments: 'Rating Scales',
};

// ─── Human-readable labels for change types ─────────────────────────────
const CHANGE_TYPE_LABELS = {
  org_config_update: 'Settings updated',
  notification_config_update: 'Notifications updated',
  logo_upload: 'Logo uploaded',
  logo_delete: 'Logo removed',
};

// ─── Human-readable field labels for org config sections ────────────────
// Flat field keys used in supportContacts, letterhead, branding sections.
const ORG_FIELD_LABELS = {
  phone: 'Phone',
  email: 'Email',
  website: 'Website',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  addressLine3: 'Address Line 3',
  addressLine4: 'Address Line 4 (Postcode)',
  primaryColor: 'Primary Brand Colour',
  secondaryColor: 'Secondary Brand Colour',
  partnershipText: 'Partnership Text',
  logoUrl: 'Organisation Logo',
};

// ─── Human-readable labels for notification toggle keys ─────────────────
// Structured as groupKey.notifKey → label.  The ".send" / ".ccProvider"
// suffixes are stripped by the renderer and shown as separate badges.
const NOTIF_KEY_LABELS = {
  'patient.patient_welcome': 'Patient: Welcome Email (Password Setup Link)',
  'patient.patient_setup_success': 'Patient: Account Setup Confirmation',
  'patient.account_setup_reminder_day_3': 'Patient: Account Setup – Day 3',
  'patient.account_setup_reminder_day_6': 'Patient: Account Setup – Day 6',
  'patient.account_setup_reminder_day_9': 'Patient: Account Setup – Day 9',
  'patient.questionnaire_reminder_day_3': 'Patient: Questionnaire Reminder – Day 3',
  'patient.questionnaire_reminder_day_6': 'Patient: Questionnaire Reminder – Day 6',
  'patient.questionnaire_reminder_day_9': 'Patient: Questionnaire Reminder – Day 9',
  'informant.informant_welcome': 'Informant: Welcome Email (Password Setup Link)',
  'informant.informant_setup_success': 'Informant: Account Setup Confirmation',
  'informant.informant_account_setup_reminder_day_3': 'Informant: Account Setup – Day 3',
  'informant.informant_account_setup_reminder_day_6': 'Informant: Account Setup – Day 6',
  'informant.informant_account_setup_reminder_day_9': 'Informant: Account Setup – Day 9',
  'informant.informant_questionnaire_reminder_day_3': 'Informant: Questionnaire Reminder – Day 3',
  'informant.informant_questionnaire_reminder_day_6': 'Informant: Questionnaire Reminder – Day 6',
  'informant.informant_questionnaire_reminder_day_9': 'Informant: Questionnaire Reminder – Day 9',
  'cypGuardian.cyp_guardian_welcome': 'CYP Parent/Carer: Welcome Email (Password Setup Link)',
  'cypGuardian.cyp_guardian_account_setup_reminder_day_3': 'CYP Parent/Carer: Account Setup – Day 3',
  'cypGuardian.cyp_guardian_account_setup_reminder_day_6': 'CYP Parent/Carer: Account Setup – Day 6',
  'cypGuardian.cyp_guardian_account_setup_reminder_day_9': 'CYP Parent/Carer: Account Setup – Day 9',
  'cypGuardian.cyp_guardian_questionnaire_reminder_day_3': 'CYP Parent/Carer: Questionnaire Reminder – Day 3',
  'cypGuardian.cyp_guardian_questionnaire_reminder_day_6': 'CYP Parent/Carer: Questionnaire Reminder – Day 6',
  'cypGuardian.cyp_guardian_questionnaire_reminder_day_9': 'CYP Parent/Carer: Questionnaire Reminder – Day 9',
  'cypSchoolRep.cyp_school_rep_welcome': 'CYP School Representative: Nomination Welcome Email',
  'cypSchoolRep.cyp_school_rep_account_setup_reminder_day_3': 'CYP School Representative: Account Setup – Day 3',
  'cypSchoolRep.cyp_school_rep_account_setup_reminder_day_6': 'CYP School Representative: Account Setup – Day 6',
  'cypSchoolRep.cyp_school_rep_account_setup_reminder_day_9': 'CYP School Representative: Account Setup – Day 9',
  'cypSchoolRep.cyp_school_rep_questionnaire_reminder_day_3': 'CYP School Representative: Questionnaire Reminder – Day 3',
  'cypSchoolRep.cyp_school_rep_questionnaire_reminder_day_6': 'CYP School Representative: Questionnaire Reminder – Day 6',
  'cypSchoolRep.cyp_school_rep_questionnaire_reminder_day_9': 'CYP School Representative: Questionnaire Reminder – Day 9',
  'providerAlerts.patient_first_login': 'Provider Alert: Patient First Login',
  'providerAlerts.login_summary': 'Provider Alert: Login Summary',
  'providerAlerts.patient_questionnaires_completed': 'Provider Alert: Patient Questionnaires Completed',
  'providerAlerts.informant_questionnaires_completed': 'Provider Alert: Informant Questionnaires Completed',
  'providerAlerts.comprehensive_questionnaires_completed': 'Provider Alert: All Questionnaires Completed',
  'providerAlerts.informant_setup_completed': 'Provider Alert: Informant Setup Completed',
  'providerAlerts.informant_added': 'Provider Alert: Informant Added',
  'providerAlerts.informant_reassigned': 'Provider Alert: Informant Reassigned',
  'providerAlerts.informant_replaced': 'Provider Alert: Informant Replaced',
  'providerAlerts.informant_email_changed': 'Provider Alert: Informant Email Changed',
  'providerAlerts.ssi_completed': 'Provider Alert: SSI Completed',
  'providerAlerts.pad_completed': 'Provider Alert: PAD Completed',
  'providerAlerts.cyp_ssi_completed': 'Provider Alert: CYP ADHD MDT Information Available',
  'providerAlerts.cyp_pad_completed': 'Provider Alert: CYP ADHD Assessment Completed',
  'providerAlerts.self_harm_concern': 'Provider Alert: Self-Harm Concern',
};

// ─── Human-readable labels for instrument / rating scale keys ───────────
// Keys may arrive in two formats depending on whether the audit event was
// written before or after the module-keyed migration:
//   Legacy:  "patientScales.phq9"
//   Current: "adultAdhd.patientScales.phq9"
// The resolver below strips the module prefix before lookup so both resolve.
const INSTRUMENT_KEY_LABELS = {
  'patientScales.asrs': 'ASRS (Current ADHD Symptoms)',
  'patientScales.baarsIVCurrent': 'BAARS-IV (Current ADHD Symptoms)',
  'patientScales.baarsIVChildhood': 'BAARS-IV (Childhood ADHD Symptoms)',
  'patientScales.phq9': 'PHQ-9 (Depression)',
  'patientScales.gad7': 'GAD-7 (Anxiety)',
  'patientScales.aq10': 'AQ-10 (Autism)',
  'patientScales.aq50': 'AQ-50 (Autism - Extended)',
  'patientScales.ess': 'ESS (Sleepiness)',
  'patientScales.mdq': 'MDQ (Mood Disorder)',
  'patientScales.ybocs': 'Y-BOCS (OCD)',
  'patientScales.core10': 'CORE-10 (Psychological Distress)',
  'patientScales.cnst': 'Combined Neurodevelopmental Screening Tool',
  'patientScales.spat': 'Sensory Profile Assessment Tool',
  'patientScales.catq': 'CAT-Q (Camouflaging Autistic Traits)',
  'patientScales.auditc': 'AUDIT-C (Alcohol Use)',
  'patientScales.snapIV26Informant': 'SNAP-IV 26 (ADHD Symptoms)',
  informantAdulthoodInstrument: 'Adulthood Informant Instrument',
  informantChildhoodInstrument: 'Childhood Informant Instrument',
};

// Module prefixes that can appear on instrument audit keys
const INSTRUMENT_MODULE_PREFIXES = ['adultAdhd.', 'adultAutism.', 'cypAdhd.'];

// Module labels for display when the key includes a pathway prefix
const INSTRUMENT_MODULE_LABELS = {
  adultAdhd: 'Adult ADHD',
  adultAutism: 'Adult Autism',
  cypAdhd: 'CYP ADHD',
};

// Mapping from instrument value keys to human-readable instrument names
const INSTRUMENT_VALUE_LABELS = {
  baarsIVCurrentInformant: 'BAARS-IV Informant (Current)',
  asrsOAdulthoodInformant: 'ASRS-O Informant (Current)',
  baarsIVChildhoodInformant: 'BAARS-IV Informant (Childhood)',
  snapIVChildhoodInformant: 'SNAP-IV Informant (Childhood)',
  snapIV26Informant: 'SNAP-IV 26 (ADHD Symptoms)',
};

/**
 * Resolve a human-readable label for an audit field key.
 * Tries org fields → notification keys → instrument keys, falling back
 * to the raw key when no mapping exists.
 */
function resolveFieldLabel(fieldKey, section) {
  if (ORG_FIELD_LABELS[fieldKey]) return ORG_FIELD_LABELS[fieldKey];

  // For notification fields the key format is "group.notifKey.toggleField"
  // We strip the toggle suffix (.send / .ccProvider) and look up the base.
  if (section === 'notificationConfig') {
    const parts = fieldKey.split('.');
    if (parts.length === 3) {
      const baseKey = `${parts[0]}.${parts[1]}`;
      const toggleType = parts[2] === 'ccProvider' ? 'CC Provider' : 'Send';
      const baseName = NOTIF_KEY_LABELS[baseKey] || baseKey;
      return `${baseName} - ${toggleType}`;
    }
  }

  if (INSTRUMENT_KEY_LABELS[fieldKey]) return INSTRUMENT_KEY_LABELS[fieldKey];

  // Module-keyed instrument fields arrive as "adultAdhd.patientScales.phq9"
  // or "adultAdhd.informantAdulthoodInstrument". Strip the module prefix and
  // resolve the remainder against the label map, prepending the pathway name.
  for (const prefix of INSTRUMENT_MODULE_PREFIXES) {
    if (fieldKey.startsWith(prefix)) {
      const remainder = fieldKey.slice(prefix.length);
      const moduleName = INSTRUMENT_MODULE_LABELS[prefix.slice(0, -1)] || '';
      const baseLabel = INSTRUMENT_KEY_LABELS[remainder];
      if (baseLabel) {
        return moduleName ? `${baseLabel} (${moduleName})` : baseLabel;
      }
    }
  }

  return fieldKey;
}

/**
 * Format a value for display in the diff view.  Booleans become ON/OFF
 * badges, colours get swatches, and strings are shown as-is.
 */
function formatValue(value, fieldKey) {
  if (typeof value === 'boolean') {
    return value ? (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">ON</span>
    ) : (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-600">OFF</span>
    );
  }

  // Instrument value keys → human-readable names
  if (INSTRUMENT_VALUE_LABELS[value]) {
    return <span className="text-xs text-gray-700">{INSTRUMENT_VALUE_LABELS[value]}</span>;
  }

  // Hex colour values get a small swatch
  if (typeof value === 'string' && /^#([0-9A-Fa-f]{3}){1,2}$/.test(value)) {
    return (
      <span className="inline-flex items-center gap-1">
        <span
          className="inline-block w-3 h-3 rounded border border-gray-300 flex-shrink-0"
          style={{ backgroundColor: value }}
        />
        <span className="text-xs text-gray-700">{value}</span>
      </span>
    );
  }

  if (value === '' || value === null || value === undefined) {
    return <span className="text-xs text-gray-400 italic">(empty)</span>;
  }

  return <span className="text-xs text-gray-700">{String(value)}</span>;
}

/**
 * Format an ISO timestamp into a human-readable date/time string.
 */
function formatTimestamp(isoString) {
  if (!isoString) return 'Unknown date';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

/**
 * Build grouped items for the diff view.  For notification config events,
 * toggle fields (send / ccProvider) sharing the same notification key are
 * merged under a single heading.  All other sections render one item per
 * field as before.
 *
 * Returns an array of { label, changes: [{ toggleLabel, oldVal, newVal }] }.
 */
function buildDiffItems(event) {
  const { previousValues, newValues, updatedFields, section } = event;
  if (!previousValues || !newValues || !updatedFields?.length) return [];

  if (section === 'notificationConfig') {
    // Group by base notification key (group.notifKey)
    const grouped = {};
    const order = [];
    for (const fieldKey of updatedFields) {
      const parts = fieldKey.split('.');
      if (parts.length === 3) {
        const baseKey = `${parts[0]}.${parts[1]}`;
        const toggleType = parts[2] === 'ccProvider' ? 'CC Provider' : 'Send';
        if (!grouped[baseKey]) {
          grouped[baseKey] = [];
          order.push(baseKey);
        }
        grouped[baseKey].push({
          toggleLabel: toggleType,
          oldVal: previousValues[fieldKey],
          newVal: newValues[fieldKey],
        });
      } else {
        // Shouldn't happen, but handle gracefully
        const key = fieldKey;
        if (!grouped[key]) {
          grouped[key] = [];
          order.push(key);
        }
        grouped[key].push({
          toggleLabel: null,
          oldVal: previousValues[fieldKey],
          newVal: newValues[fieldKey],
        });
      }
    }
    return order.map((baseKey) => ({
      label: NOTIF_KEY_LABELS[baseKey] || baseKey,
      changes: grouped[baseKey],
    }));
  }

  // Non-notification sections: one item per field, with special handling
  // for informant instrument fields — expand them into ON → OFF / OFF → ON
  // toggle rows so they look identical to patient scale changes.
  const INFORMANT_FIELDS = new Set(['informantAdulthoodInstrument', 'informantChildhoodInstrument']);

  return updatedFields.map((fieldKey) => {
    const oldVal = previousValues[fieldKey];
    const newVal = newValues[fieldKey];

    if (INFORMANT_FIELDS.has(fieldKey) && typeof oldVal === 'string' && typeof newVal === 'string') {
      const changes = [];
      if (oldVal) {
        changes.push({
          toggleLabel: INSTRUMENT_VALUE_LABELS[oldVal] || oldVal,
          oldVal: true,
          newVal: false,
        });
      }
      if (newVal) {
        changes.push({
          toggleLabel: INSTRUMENT_VALUE_LABELS[newVal] || newVal,
          oldVal: false,
          newVal: true,
        });
      }
      return {
        label: INSTRUMENT_KEY_LABELS[fieldKey] || fieldKey,
        changes,
      };
    }

    return {
      label: resolveFieldLabel(fieldKey, section),
      changes: [{ toggleLabel: null, oldVal, newVal }],
    };
  });
}

/**
 * Render a stacked before → after diff for a single audit event.
 *
 * For notification events, toggles are grouped under their parent
 * notification name so Send and CC Provider appear as compact sub-rows:
 *
 *   Patient: Account Setup – Day 9:
 *     Send: ON → OFF
 *     CC Provider: ON → OFF
 *
 * Other sections show one row per field:
 *   Phone:
 *     old value → new value
 */
function DiffList({ event }) {
  const items = buildDiffItems(event);
  if (items.length === 0) return null;

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded p-2 space-y-2">
      {items.map((item, i) => (
        <div key={i}>
          <p className="text-[11px] font-medium text-gray-500">
            {item.label}:
          </p>
          {item.changes.map((change, j) => (
            <div key={j} className="flex items-center gap-1.5 mt-0.5 ml-1 flex-wrap">
              {change.toggleLabel && (
                <span className="text-[11px] text-gray-500 mr-0.5">{change.toggleLabel}:</span>
              )}
              <span className="text-xs text-gray-500">{formatValue(change.oldVal)}</span>
              <span className="text-gray-400 text-xs flex-shrink-0">&rarr;</span>
              <span className="text-xs font-medium">{formatValue(change.newVal)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Fallback field list for older events that don't have previousValues /
 * newValues (written before the audit upgrade).
 */
function LegacyFieldList({ event }) {
  const fields = event.updatedFields || [];
  if (fields.length === 0) return null;

  // If previousValues/newValues exist, DiffTable handles rendering
  if (event.previousValues && event.newValues) return null;

  return (
    <div className="mt-1.5">
      <p className="text-xs font-medium text-gray-500 mb-0.5">Fields changed:</p>
      <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
        {fields.slice(0, 10).map((field, i) => (
          <li key={i} className="truncate">{resolveFieldLabel(field, event.section)}</li>
        ))}
        {fields.length > 10 && (
          <li className="text-gray-400 italic">... and {fields.length - 10} more</li>
        )}
      </ul>
    </div>
  );
}


export default function ConfigHistoryTimeline({ sections, onBack, title = 'Change History', onContentChange }) {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Stable stringified key so the effect only re-runs when the actual
  // section values change, not on every render of a new array reference.
  const sectionsKey = JSON.stringify(sections);

  useEffect(() => {
    let cancelled = false;

    const parsedSections = JSON.parse(sectionsKey);

    async function fetchHistory() {
      setIsLoading(true);
      setError('');
      try {
        const getHistory = httpsCallable(functions, 'getOrgConfigHistory');
        const result = await getHistory({ sections: parsedSections, limit: 50 });
        if (!cancelled) {
          setEvents(result.data?.events || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[ConfigHistoryTimeline] Fetch error:', err);
          setError('Unable to load change history. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          if (onContentChange) {
            setTimeout(onContentChange, 50);
          }
        }
      }
    }

    fetchHistory();
    return () => { cancelled = true; };
  }, [sectionsKey, onContentChange]);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-primary hover:text-primary/80 font-semibold"
      >
        &larr; Back to Overview
      </button>

      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="text-xs text-gray-500">
        Most recent configuration changes for this section.
      </p>

      {isLoading && (
        <div className="space-y-3 animate-pulse py-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-2 h-2 mt-1.5 rounded-full bg-gray-200 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-200 rounded w-1/3" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {!isLoading && !error && events.length === 0 && (
        <div className="py-6 text-center">
          <p className="text-sm text-gray-400 italic">No changes recorded yet.</p>
        </div>
      )}

      {!isLoading && !error && events.length > 0 && (
        <div className="max-h-[65vh] overflow-y-auto pr-1">
          {/* Inner content wrapper is the positioning context for the
              timeline line - same pattern as DocumentsDashboard.js.
              Placing the line here (not on the scroll container) ensures
              it spans the full content height, not just the viewport. */}
          <div className="relative space-y-4 pb-2">
            {events.length > 1 && (
              <div className="absolute left-[5px] top-[9px] bottom-0 w-px bg-gray-200 z-0" />
            )}

            {events.map((event, idx) => {
              const sectionLabel = SECTION_LABELS[event.section] || event.section;
              const changeLabel = CHANGE_TYPE_LABELS[event.changeType] || event.changeType;
              const hasDiff = event.previousValues && event.newValues && event.updatedFields?.length;
              const isLast = idx === events.length - 1;

              return (
                <div key={event.id} className="relative flex gap-3 pl-0">
                  {/* Timeline dot column - z-10 so dot and mask sit on top of the line */}
                  <div className="relative z-10 w-[11px] flex-shrink-0 flex flex-col items-center pt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary border-2 border-white shadow-sm flex-shrink-0" />
                    {/* On the last event, a white strip covers the line below
                        the dot so it doesn't overshoot past the final card. */}
                    {isLast && <div className="flex-1 w-[3px] bg-white" />}
                  </div>

                  {/* Event card */}
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-md p-2.5 min-w-0">
                    {/* Header: timestamp + section badge */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-[11px] text-gray-400">
                        {formatTimestamp(event.occurredAt)}
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary whitespace-nowrap flex-shrink-0">
                        {sectionLabel}
                      </span>
                    </div>

                    {/* Change type */}
                    <p className="text-xs font-semibold text-gray-800">{changeLabel}</p>

                    {/* Who made the change */}
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      by {event.performedByName || event.performedByEmail || event.performedBy || 'Unknown'}
                      {event.userType && (
                        <span className="ml-1 text-gray-400">({event.userType})</span>
                      )}
                    </p>

                    {/* Before/after diff (new events with detailed data) */}
                    {hasDiff && <DiffList event={event} />}

                    {/* Fallback: plain field list for older events */}
                    {!hasDiff && <LegacyFieldList event={event} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
